import type { ExtractPreviewResponse } from '@/shared/hooks/api/userStandards'
import {
  resolveHazardAvailableLocales,
  resolveLocalizedHazardField,
} from '@/shared/lib/standardEntityLocalizations'
import { useEntityDisplayLocales } from '../../hooks/useEntityDisplayLocales'
import { StandardEntityLocaleToggle } from '../StandardEntityLocaleToggle'
import {
  ExtractionPreviewImpedimentBanner,
  ExtractionPreviewNoData,
  ExtractionPreviewSectionNotFound,
  type SectionExtractionPreviewContentProps,
} from './standardExtractionPreviewShared'

type StandardSignificantHazardsExtractionPreviewContentProps =
  SectionExtractionPreviewContentProps & {
    preview: ExtractPreviewResponse
  }

export function StandardSignificantHazardsExtractionPreviewContent({
  preview,
  contentVariant = 'display',
  uiState,
  resolveReason,
  translate,
}: StandardSignificantHazardsExtractionPreviewContentProps) {
  const { getDisplayLocale, selectItemLocale } = useEntityDisplayLocales()
  const useSourceFields = contentVariant === 'source'
  const hazards = preview.significantHazards?.hazards ?? []
  const hazardsImpediment = preview.significantHazardsImpediment ?? null

  if (uiState.sectionNotFound) {
    return (
      <ExtractionPreviewSectionNotFound
        reasonCode={hazardsImpediment?.reason ?? 'hazards_not_found'}
        resolveReason={resolveReason}
        translate={translate}
      />
    )
  }
  if (hazards.length > 0) {
    return (
      <ul className='space-y-0 divide-y'>
        {hazards.map((hazard) => {
          const availableLocales = resolveHazardAvailableLocales(hazard)
          const itemLocale = getDisplayLocale(hazard.id)
          const body =
            (useSourceFields
              ? hazard.description
              : resolveLocalizedHazardField(
                  hazard,
                  itemLocale,
                  'description'
                )) || '—'
          return (
            <li
              key={hazard.id}
              className='flex items-start justify-between gap-3 py-2 first:pt-0 last:pb-0 first:last:py-0'
            >
              <div className='min-w-0 flex-1'>
                {hazard.number != null && hazard.number !== '' && (
                  <span className='font-medium'>{hazard.number}. </span>
                )}
                <span className='text-sm leading-loose whitespace-pre-wrap'>
                  {body}
                </span>
              </div>
              {!useSourceFields && (
                <StandardEntityLocaleToggle
                  availableLocales={availableLocales}
                  selectedLocale={itemLocale}
                  onSelectLocale={(locale) => selectItemLocale(hazard.id, locale)}
                />
              )}
            </li>
          )
        })}
      </ul>
    )
  }
  if (hazardsImpediment != null && hazardsImpediment.reason) {
    return (
      <ExtractionPreviewImpedimentBanner
        label={translate('detail.extractPreview.noHazardsExtracted')}
        reason={resolveReason(hazardsImpediment.reason)}
      />
    )
  }
  return <ExtractionPreviewNoData translate={translate} />
}
